import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { TranslationModule } from '@core/translation/translation.module';
import {
    BcmDynamicPriceDto,
    DynamicPriceCalculation,
    DynamicPriceGroupBreakdown,
    DynamicPriceRuleEvaluation,
} from '@shared/models/bcm-dynamic-price';

import { formatMoney } from '../../utils/booking-v2-pricing';

// Read-only breakdown of how a dynamic price was reached, from the already-stored
// evaluation result (`dynamicPrice`). Mirrors the legacy product-dynamic-price view
// (groups table for the v2 engine, condition/calculation tables for legacy) but
// purely presentational — no form wiring. Used inline (expandable) on each dynamic
// relation product and service line.
@Component({
    selector: 'booking-v2-dynamic-price-breakdown',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TranslationModule],
    templateUrl: './booking-v2-dynamic-price-breakdown.component.html',
    host: { class: 'block' },
})
export class BookingV2DynamicPriceBreakdownComponent {

    readonly dynamicPrice = input<Partial<BcmDynamicPriceDto> | null>(null);

    // v2 groups engine takes precedence (presence of `groups`); then the legacy
    // condition-evaluation table; then the legacy calculation chain.
    readonly groups = computed<DynamicPriceGroupBreakdown[]>(() => this.dynamicPrice()?.ruleCalculations?.groups ?? []);
    readonly evaluations = computed<DynamicPriceRuleEvaluation[]>(() => this.dynamicPrice()?.ruleEvaluations?.evaluations ?? []);
    readonly calculations = computed<DynamicPriceCalculation[]>(() => this.dynamicPrice()?.ruleCalculations?.calculations ?? []);

    readonly mode = computed<'v2' | 'evaluations' | 'calculations' | 'empty'>(() => {
        if (this.groups().length) {
            return 'v2';
        }
        if (this.evaluations().length) {
            return 'evaluations';
        }
        if (this.calculations().length) {
            return 'calculations';
        }
        return 'empty';
    });

    readonly ruleName = computed(() => this.dynamicPrice()?.ruleName ?? null);
    readonly total = computed(() => this.dynamicPrice()?.rulePrice ?? null);

    fmt = formatMoney;
}
